import QRCode from "qrcode";
import {
  bodyPositions,
  directionLabel,
  normalizeDegrees,
  riseSetSummaryForStar,
  starHorizontal,
  starPositions,
  timelineMarkers,
  visibilityPhrase,
} from "../core/astro";
import { loadCatalog } from "../core/catalog";
import { encodeHandoff } from "../core/handoff";
import { bestTargetStars, buildSearchResults, visibleNowObjects, type SearchResult } from "../core/search";
import { loadTargetList, saveTargetList } from "../core/storage";
import type { BodyPosition, ObserverLocation, SkyCatalog, SkyObjectPosition, TargetListItem } from "../core/types";
import type { StarPosition } from "../core/types";
import { drawSky, nearestCanvasObject, type CanvasHit, type SkyViewportState } from "./skyCanvas";
import {
  DefaultViewport,
  interpolateViewport,
  panViewport,
  targetViewportForObject,
  zoomViewport,
} from "./viewport";

const DefaultObserver: ObserverLocation = {
  label: "Jerusalem",
  lat: 31.7683,
  lon: 35.2137,
};

const LocationPresets: ObserverLocation[] = [
  DefaultObserver,
  { label: "Mitzpe Ramon", lat: 30.6102, lon: 34.8019 },
  { label: "Tel Aviv", lat: 32.0853, lon: 34.7818 },
  { label: "New York", lat: 40.7128, lon: -74.006 },
  { label: "London", lat: 51.5072, lon: -0.1276 },
  { label: "Sydney", lat: -33.8688, lon: 151.2093 },
  { label: "Atacama", lat: -24.589, lon: -69.191 },
];

type ExperienceMode = "beginner" | "advanced";
type WorkspaceTab = "tonight" | "search" | "object" | "plan" | "settings";

interface AppState {
  catalog: SkyCatalog;
  catalogVerified: boolean;
  observer: ObserverLocation;
  viewDate: Date;
  isPlaying: boolean;
  showGuideLines: boolean;
  showBelowHorizon: boolean;
  minAltitudeDeg: number;
  maxMagnitude: number;
  controlsCollapsed: boolean;
  mode: ExperienceMode;
  activeTab: WorkspaceTab;
  activeTargetIndex: number;
  notice?: string;
  query: string;
  selected?: SkyObjectPosition;
  targetList: TargetListItem[];
  viewport: SkyViewportState;
  skyCache?: SkyComputedState;
}

interface SkyComputedState {
  key: string;
  stars: StarPosition[];
  bodies: ReturnType<typeof bodyPositions>;
  objects: SkyObjectPosition[];
}

interface AppComputedState {
  stars: StarPosition[];
  bodies: BodyPosition[];
  objects: SkyObjectPosition[];
  searchResults: SearchResult[];
  visibleNow: SkyObjectPosition[];
  bestTargets: StarPosition[];
}

interface ConstellationPick {
  id: string;
  name: string;
  shapeHint: string;
  altitudeDeg: number;
  visibleStars: number;
}

const PlaybackStepMillis = 10 * 60 * 1000;
const PlaybackIntervalMillis = 700;
let playbackTimer: number | undefined;
let focusAnimationFrame: number | undefined;

const Icons: Record<string, string> = {
  zoomIn: '<path d="M11 4v14M4 11h14"/>',
  zoomOut: '<path d="M4 11h14"/>',
  focus:
    '<circle cx="11" cy="11" r="3"/><path d="M11 2v4M11 16v4M2 11h4M16 11h4"/>',
  guides: '<circle cx="11" cy="11" r="8"/><path d="M3 11h16M11 3v16"/>',
  eye: '<path d="M3 11c2-4 5.5-6 8-6s6 2 8 6c-2 4-5.5 6-8 6s-6-2-8-6Z"/><circle cx="11" cy="11" r="2.4"/>',
  reset: '<path d="M16 7a6 6 0 1 0 1.6 5.8M16 3v5h-5"/>',
  play: '<path d="M6 4l12 7-12 7V4Z" fill="currentColor" stroke="none"/>',
  pause: '<path d="M6 4h3v14H6V4ZM12 4h3v14h-3V4Z" fill="currentColor" stroke="none"/>',
  prev: '<path d="M14 5 7 11l7 6"/>',
  next: '<path d="M8 5l7 6-7 6"/>',
  skipStart: '<path d="M7 5v12M16 6l-7 5 7 5"/>',
  now: '<circle cx="11" cy="11" r="8"/><path d="M11 7v4l3 2"/>',
  up: '<path d="M5 13l6-6 6 6"/>',
  down: '<path d="M5 9l6 6 6-6"/>',
  remove: '<path d="M6 6l10 10M16 6 6 16"/>',
  plus: '<path d="M11 5v12M5 11h12"/>',
  check: '<path d="M5 11l4 4 8-9"/>',
};

function icon(name: keyof typeof Icons): string {
  return `<svg class="icon" viewBox="0 0 22 22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${Icons[name]}</svg>`;
}

export async function bootApp(root: HTMLElement): Promise<void> {
  root.innerHTML = `
    <main class="boot-error">
      <h1>Loading SkyView Web</h1>
      <p>Loading the offline star catalog...</p>
    </main>
  `;
  const catalogLoad = await loadCatalog();
  const state: AppState = {
    catalog: catalogLoad.catalog,
    catalogVerified: catalogLoad.verified,
    observer: DefaultObserver,
    viewDate: new Date(),
    isPlaying: false,
    showGuideLines: true,
    showBelowHorizon: false,
    minAltitudeDeg: 20,
    maxMagnitude: 4.5,
    controlsCollapsed: false,
    mode: "beginner",
    activeTab: "tonight",
    activeTargetIndex: 0,
    query: "",
    targetList: [],
    viewport: DefaultViewport,
  };
  render(root, state);
  void loadTargetList()
    .then((items) => {
      state.targetList = items;
      render(root, state);
    })
    .catch(() => {
      state.targetList = [];
    });
}

function render(root: HTMLElement, state: AppState): void {
  const computed = computeState(state);
  state.selected = resolveSelected(state.selected, computed.objects, state.catalog, state.viewport);
  const selected = state.selected;
  const targetIds = state.targetList.map((item) => item.hr);
  const selectedStar = selected?.kind === "star" ? (selected as StarPosition) : undefined;
  const riseSet = selectedStar ? riseSetSummaryForStar(selectedStar.star, state.observer, state.viewDate) : undefined;
  const relativeHours = relativeHoursFromNow(state.viewDate);
  const handoff = handoffForState(state);

  root.innerHTML = `
    <div class="app-shell ${state.controlsCollapsed ? "controls-collapsed" : ""}">
      <section class="sky-stage" aria-label="Interactive sky planner">
        <canvas id="sky-canvas" aria-label="Projected night sky"></canvas>
        <div class="topbar">
          <div>
            <p class="eyebrow">SkyView Web</p>
            <h1>Tonight's Sky Lab</h1>
          </div>
          <div class="status-row">
            <span class="status-chip ${state.catalogVerified ? "ok" : "warn"}">${state.catalogVerified ? "Catalog verified" : "Checksum warning"}</span>
            <span class="status-chip">${state.catalog.stars.length.toLocaleString()} stars</span>
            <span class="status-chip">${state.mode === "beginner" ? "Beginner" : "Advanced"}</span>
          </div>
        </div>
        <div class="sky-controls" aria-label="Sky controls">
          <div class="toolbar-group">
            <button id="sky-zoom-in" type="button" title="Zoom in">${icon("zoomIn")}</button>
            <button id="sky-zoom-out" type="button" title="Zoom out">${icon("zoomOut")}</button>
          </div>
          <div class="toolbar-group">
            <button id="sky-focus-button" type="button" title="Focus selected object" ${selected ? "" : "disabled"}>${icon("focus")}</button>
            <button id="sky-guides-button" type="button" title="Toggle guide lines" aria-pressed="${state.showGuideLines}">${icon("guides")}</button>
            <button id="sky-below-button" type="button" title="Toggle below-horizon objects" aria-pressed="${state.showBelowHorizon}">${icon("eye")}</button>
          </div>
          <div class="toolbar-group">
            <button id="sky-reset-button" type="button" title="Reset view">${icon("reset")}</button>
          </div>
        </div>
        ${bottomTimeRailTemplate(state, relativeHours)}
        ${state.controlsCollapsed ? `
          <button
            id="workspace-open"
            class="workspace-open"
            type="button"
            aria-expanded="false"
            aria-controls="workspace-panel"
          >
            Open controls
          </button>
        ` : ""}
      </section>
      ${state.controlsCollapsed ? "" : drawerTemplate(state, computed, selected, riseSet, targetIds, handoff, relativeHours)}
    </div>
  `;

  const canvas = root.querySelector<HTMLCanvasElement>("#sky-canvas");
  if (canvas) {
    const canvasHits = drawSky({
      canvas,
      catalog: state.catalog,
      stars: computed.stars,
      bodies: computed.bodies,
      selected,
      targetHrs: targetIds,
      viewport: state.viewport,
      showGuideLines: state.showGuideLines,
      showBelowHorizon: state.showBelowHorizon,
    });
    bindCanvasInteractions(root, canvas, state, canvasHits);
  }

  bindControls(root, state);
  renderQr(root, handoff);
}

function computeState(state: AppState): AppComputedState {
  const { stars, bodies, objects } = computeSkyState(state);
  return {
    stars,
    bodies,
    objects,
    searchResults: buildSearchResults(state.query, state.catalog, stars, bodies),
    visibleNow: visibleNowObjects(objects),
    bestTargets: bestTargetStars(stars, {
      minAltitudeDeg: state.minAltitudeDeg,
      maxMagnitude: state.maxMagnitude,
      limit: 16,
    }),
  };
}

function bottomTimeRailTemplate(state: AppState, relativeHours: number): string {
  return `
    <div class="time-rail" aria-label="Time controls">
      <div class="time-rail-meta">
        <span>Planning time</span>
        <strong>${formatSkyDateTime(state.viewDate)}</strong>
        <small>${escapeHtml(state.observer.label)} | ${state.observer.lat.toFixed(2)}, ${state.observer.lon.toFixed(2)}</small>
      </div>
      <div class="time-rail-actions">
        <button id="bottom-step-back" type="button" title="Back 1 hour">${icon("prev")}</button>
        <button id="bottom-play-button" class="primary" type="button" title="${state.isPlaying ? "Pause" : "Play"}" aria-pressed="${state.isPlaying}">${state.isPlaying ? icon("pause") : icon("play")}</button>
        <button id="bottom-step-forward" type="button" title="Forward 1 hour">${icon("next")}</button>
        <button id="bottom-now-button" type="button" title="Jump to now">${icon("now")}</button>
      </div>
      <input id="bottom-timeline-input" type="range" min="-12" max="12" step="0.25" value="${relativeHours.toFixed(2)}" aria-label="Scrub time by hours from now" />
      <div class="time-rail-presets">
        ${timePresetButtons(state.viewDate)}
      </div>
    </div>
  `;
}

function drawerTemplate(
  state: AppState,
  computed: AppComputedState,
  selected: SkyObjectPosition | undefined,
  riseSet: { rise?: Date; set?: Date; transit?: Date } | undefined,
  targetIds: number[],
  handoff: string,
  relativeHours: number,
): string {
  const tabs: Array<{ id: WorkspaceTab; label: string; badge?: number }> = [
    { id: "tonight", label: "Tonight" },
    { id: "search", label: "Search" },
    { id: "object", label: "Object" },
    { id: "plan", label: "Plan", badge: state.targetList.length || undefined },
    { id: "settings", label: "Settings" },
  ];

  return `
    <aside id="workspace-panel" class="workspace" aria-label="Planning controls" aria-hidden="${state.controlsCollapsed}">
      <div class="workspace-header">
        <div>
          <span>Observation Planner</span>
          <small>Tonight, search, targets</small>
        </div>
        <button id="workspace-close" class="icon-button" type="button" aria-expanded="true" aria-controls="workspace-panel" title="Close planner">${icon("remove")}</button>
      </div>
      <div class="workspace-tabs" role="tablist" aria-label="Planner sections">
        ${tabs
          .map(
            (tab) => `
          <button type="button" class="workspace-tab" data-tab="${tab.id}" role="tab" aria-selected="${state.activeTab === tab.id}">
            ${escapeHtml(tab.label)}${tab.badge ? `<span class="tab-badge">${tab.badge}</span>` : ""}
          </button>
        `,
          )
          .join("")}
      </div>

      ${state.activeTab === "tonight" ? tonightPanelTemplate(state, computed, targetIds) : ""}
      ${state.activeTab === "search" ? searchPanelTemplate(state, computed) : ""}
      ${
        state.activeTab === "object"
          ? `
        <section class="panel object-panel">
          ${selected ? selectedTemplate(selected, state.catalog, riseSet, state.observer, state.viewDate, state.mode) : emptySelectionTemplate()}
        </section>
      `
          : ""
      }
      ${state.activeTab === "plan" ? targetPlanTemplate(state, computed.objects, handoff) : ""}
      ${state.activeTab === "settings" ? settingsPanelTemplate(state, relativeHours) : ""}
    </aside>
  `;
}

function tonightPanelTemplate(state: AppState, computed: AppComputedState, targetIds: number[]): string {
  const hero = computed.bestTargets[0];
  const planets = computed.bodies
    .filter((body) => body.altitudeDeg >= 0)
    .sort((a, b) => a.magnitude - b.magnitude || b.altitudeDeg - a.altitudeDeg)
    .slice(0, 4);
  const constellations = visibleConstellationPicks(state.catalog, computed.stars, 4);

  return `
    <section class="panel tonight-panel">
      <div class="panel-title">
        <span>Tonight</span>
        <button id="add-best-targets" class="primary" type="button">Build starter plan</button>
      </div>
      ${hero ? `
        <button class="tonight-hero" type="button" data-select-id="${hero.id}">
          <span>Best first target</span>
          <strong>${escapeHtml(hero.label)}</strong>
          <small>${escapeHtml(viewingAdvice(hero, state.observer, state.viewDate, state.mode))}</small>
        </button>
      ` : `<p class="muted">No strong naked-eye targets match the current filters.</p>`}
      <div class="tonight-lanes">
        <div class="tonight-lane">
          <h3>Naked Eye</h3>
          ${computed.bestTargets.slice(0, 4).map((star) => observationCardTemplate(star, state, targetIds)).join("") || `<p class="muted">Raise the magnitude limit to find more targets.</p>`}
        </div>
        <div class="tonight-lane">
          <h3>Planets</h3>
          ${planets.map((body) => observationCardTemplate(body, state, targetIds)).join("") || `<p class="muted">No planets are comfortably above the horizon right now.</p>`}
        </div>
        <div class="tonight-lane">
          <h3>Constellations</h3>
          ${constellations.map((constellation) => constellationCardTemplate(constellation)).join("") || `<p class="muted">Turn on below-horizon objects or change time for more constellation picks.</p>`}
        </div>
      </div>
    </section>
  `;
}

function searchPanelTemplate(state: AppState, computed: AppComputedState): string {
  return `
    <section class="panel">
      <div class="panel-title">
        <span>Search</span>
        <button id="clear-search" type="button">Clear</button>
      </div>
      <input id="search-input" class="search-input" type="search" dir="ltr" autocomplete="off" spellcheck="false" placeholder="Search Vega, Uranus, Orion..." value="${escapeHtml(state.query)}" />
      <div class="result-list">
        ${computed.searchResults.map((result) => resultTemplate(result)).join("") || `<p class="muted">Type a star, planet, HR number, or constellation.</p>`}
      </div>
    </section>
  `;
}

function settingsPanelTemplate(state: AppState, relativeHours: number): string {
  return `
    <section class="panel settings-panel">
      <div class="panel-title">
        <span>Settings</span>
        <button id="reset-filters" type="button">Reset filters</button>
      </div>
      <div class="segmented-control" aria-label="Experience mode">
        <button id="mode-beginner" type="button" aria-pressed="${state.mode === "beginner"}">Beginner</button>
        <button id="mode-advanced" type="button" aria-pressed="${state.mode === "advanced"}">Advanced</button>
      </div>
      <label>
        Exact time
        <input id="time-input" type="datetime-local" value="${toDateTimeLocalValue(state.viewDate)}" />
      </label>
      <input id="timeline-input" type="range" min="-12" max="12" step="0.25" value="${relativeHours.toFixed(2)}" aria-label="Scrub time by hours from now" />
      <div class="timeline-markers">
        ${timelineMarkers(state.viewDate).map((marker) => `<button class="marker" data-epoch="${marker.epochMillis}" type="button">${marker.label}</button>`).join("")}
      </div>
      <div class="location-grid">
        <label>
          Latitude
          <input id="lat-input" type="number" step="0.0001" value="${state.observer.lat}" />
        </label>
        <label>
          Longitude
          <input id="lon-input" type="number" step="0.0001" value="${state.observer.lon}" />
        </label>
      </div>
      <details class="location-presets">
        <summary>Quick locations</summary>
        <div class="preset-grid">
          ${LocationPresets.map((location) => `
            <button class="preset-button ${location.label === state.observer.label ? "is-active" : ""}" type="button" data-location-label="${escapeHtml(location.label)}">${escapeHtml(location.label)}</button>
          `).join("")}
        </div>
        <button id="geo-button" class="secondary" type="button">Use browser location</button>
      </details>
      <label>
        Minimum altitude: ${state.minAltitudeDeg} deg
        <input id="min-altitude-input" type="range" min="0" max="70" step="5" value="${state.minAltitudeDeg}" />
      </label>
      <label>
        Faintest magnitude: ${state.maxMagnitude.toFixed(1)}
        <input id="max-magnitude-input" type="range" min="0" max="6.5" step="0.5" value="${state.maxMagnitude}" />
      </label>
      <div class="notice-line">${escapeHtml(state.notice ?? "Ready for a planning run.")}</div>
    </section>
  `;
}

function computeSkyState(state: AppState): SkyComputedState {
  const key = [
    state.catalog.v,
    state.catalog.stars.length,
    state.observer.lat,
    state.observer.lon,
    state.viewDate.getTime(),
  ].join("|");

  if (state.skyCache?.key === key) return state.skyCache;

  const stars = starPositions(state.catalog, state.observer, state.viewDate);
  const bodies = bodyPositions(state.observer, state.viewDate);
  const objects = [...stars, ...bodies];
  state.skyCache = { key, stars, bodies, objects };
  return state.skyCache;
}

function bindControls(root: HTMLElement, state: AppState): void {
  root.querySelector("#workspace-open")?.addEventListener("click", () => {
    state.controlsCollapsed = false;
    render(root, state);
  });
  root.querySelector("#workspace-close")?.addEventListener("click", () => {
    state.controlsCollapsed = true;
    render(root, state);
  });
  root.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((button) => {
    button.addEventListener("click", () => {
      state.activeTab = button.dataset.tab as WorkspaceTab;
      render(root, state);
    });
  });
  root.querySelector("#mode-beginner")?.addEventListener("click", () => {
    state.mode = "beginner";
    state.notice = "Beginner view shows plain-language observing guidance.";
    render(root, state);
  });
  root.querySelector("#mode-advanced")?.addEventListener("click", () => {
    state.mode = "advanced";
    state.notice = "Advanced view shows altitude, azimuth, magnitude, and timing details.";
    render(root, state);
  });
  root.querySelector("#bottom-now-button")?.addEventListener("click", () => {
    stopPlayback(state);
    state.viewDate = new Date();
    render(root, state);
  });
  root.querySelector("#bottom-play-button")?.addEventListener("click", () => {
    setPlayback(root, state, !state.isPlaying);
  });
  root.querySelector("#bottom-step-back")?.addEventListener("click", () => {
    stepTime(root, state, -60);
  });
  root.querySelector("#bottom-step-forward")?.addEventListener("click", () => {
    stepTime(root, state, 60);
  });
  root.querySelector<HTMLInputElement>("#bottom-timeline-input")?.addEventListener("input", (event) => {
    stopPlayback(state);
    const hours = Number((event.target as HTMLInputElement).value);
    state.viewDate = new Date(Date.now() + hours * 60 * 60 * 1000);
    render(root, state);
  });
  root.querySelector("#now-button")?.addEventListener("click", () => {
    stopPlayback(state);
    state.viewDate = new Date();
    render(root, state);
  });
  root.querySelector<HTMLInputElement>("#time-input")?.addEventListener("change", (event) => {
    stopPlayback(state);
    const value = (event.target as HTMLInputElement).value;
    const nextDate = new Date(value);
    if (!Number.isNaN(nextDate.getTime())) {
      state.viewDate = nextDate;
    }
    render(root, state);
  });
  root.querySelector<HTMLInputElement>("#timeline-input")?.addEventListener("input", (event) => {
    stopPlayback(state);
    const hours = Number((event.target as HTMLInputElement).value);
    state.viewDate = new Date(Date.now() + hours * 60 * 60 * 1000);
    render(root, state);
  });
  root.querySelectorAll<HTMLButtonElement>(".marker").forEach((button) => {
    button.addEventListener("click", () => {
      stopPlayback(state);
      state.viewDate = new Date(Number(button.dataset.epoch));
      state.notice = "Timeline marker applied.";
      render(root, state);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-time-preset]").forEach((button) => {
    button.addEventListener("click", () => {
      stopPlayback(state);
      state.viewDate = new Date(Number(button.dataset.timePreset));
      state.notice = `${button.textContent?.trim() ?? "Time"} preset applied.`;
      render(root, state);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-location-label]").forEach((button) => {
    button.addEventListener("click", () => {
      const location = LocationPresets.find((preset) => preset.label === button.dataset.locationLabel);
      if (!location) return;
      stopPlayback(state);
      state.observer = location;
      state.notice = `${location.label} location loaded.`;
      render(root, state);
    });
  });
  root.querySelector<HTMLInputElement>("#lat-input")?.addEventListener("change", (event) => {
    stopPlayback(state);
    state.observer = { ...state.observer, label: "Manual", lat: Number((event.target as HTMLInputElement).value) };
    state.notice = "Manual latitude applied.";
    render(root, state);
  });
  root.querySelector<HTMLInputElement>("#lon-input")?.addEventListener("change", (event) => {
    stopPlayback(state);
    state.observer = { ...state.observer, label: "Manual", lon: Number((event.target as HTMLInputElement).value) };
    state.notice = "Manual longitude applied.";
    render(root, state);
  });
  root.querySelector("#geo-button")?.addEventListener("click", () => {
    navigator.geolocation?.getCurrentPosition((position) => {
      stopPlayback(state);
      state.observer = {
        label: "Browser location",
        lat: Number(position.coords.latitude.toFixed(5)),
        lon: Number(position.coords.longitude.toFixed(5)),
      };
      state.notice = "Browser location applied.";
      render(root, state);
    });
  });
  root.querySelector<HTMLInputElement>("#min-altitude-input")?.addEventListener("input", (event) => {
    state.minAltitudeDeg = Number((event.target as HTMLInputElement).value);
    state.notice = `Targets filtered above ${state.minAltitudeDeg} deg.`;
    render(root, state);
  });
  root.querySelector<HTMLInputElement>("#max-magnitude-input")?.addEventListener("input", (event) => {
    state.maxMagnitude = Number((event.target as HTMLInputElement).value);
    state.notice = `Targets filtered to mag ${state.maxMagnitude.toFixed(1)} or brighter.`;
    render(root, state);
  });
  root.querySelector("#reset-filters")?.addEventListener("click", () => {
    state.minAltitudeDeg = 20;
    state.maxMagnitude = 4.5;
    state.notice = "Discovery filters reset.";
    render(root, state);
  });
  root.querySelector<HTMLInputElement>("#search-input")?.addEventListener("input", (event) => {
    state.query = (event.target as HTMLInputElement).value;
    refreshSearchResults(root, state);
  });
  root.querySelector("#clear-search")?.addEventListener("click", () => {
    state.query = "";
    state.selected = undefined;
    render(root, state);
  });
  root.querySelector("#sky-play-button")?.addEventListener("click", () => {
    setPlayback(root, state, !state.isPlaying);
  });
  root.querySelector("#sky-step-back")?.addEventListener("click", () => {
    stepTime(root, state, -60);
  });
  root.querySelector("#sky-step-forward")?.addEventListener("click", () => {
    stepTime(root, state, 60);
  });
  root.querySelector("#sky-zoom-in")?.addEventListener("click", () => {
    state.viewport = zoomViewport(state.viewport, true);
    redrawSkyOnly(root, state);
  });
  root.querySelector("#sky-zoom-out")?.addEventListener("click", () => {
    state.viewport = zoomViewport(state.viewport, false);
    redrawSkyOnly(root, state);
  });
  root.querySelector("#sky-focus-button")?.addEventListener("click", () => {
    if (state.selected) animateViewportTo(root, state, state.selected);
  });
  root.querySelector("#sky-guides-button")?.addEventListener("click", () => {
    state.showGuideLines = !state.showGuideLines;
    render(root, state);
  });
  root.querySelector("#sky-below-button")?.addEventListener("click", () => {
    state.showBelowHorizon = !state.showBelowHorizon;
    render(root, state);
  });
  root.querySelector("#sky-reset-button")?.addEventListener("click", () => {
    cancelFocusAnimation();
    state.viewport = DefaultViewport;
    redrawSkyOnly(root, state);
  });
  root.querySelector("#focus-selected")?.addEventListener("click", () => {
    if (state.selected) animateViewportTo(root, state, state.selected);
  });
  root.querySelectorAll<HTMLButtonElement>("[data-select-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const computed = computeState(state);
      const object = computed.objects.find((candidate) => candidate.id === button.dataset.selectId);
      if (object) {
        selectObject(root, state, object);
        return;
      }
    });
  });
  bindSearchResultButtons(root, state);
  root.querySelector("#add-target")?.addEventListener("click", async () => {
    if (state.selected?.kind !== "star" || state.selected.hr === undefined) return;
    await addTargetHr(state, state.selected.hr);
    render(root, state);
  });
  root.querySelectorAll<HTMLButtonElement>("[data-add-hr]").forEach((button) => {
    button.addEventListener("click", async () => {
      await addTargetHr(state, Number(button.dataset.addHr));
      render(root, state);
    });
  });
  root.querySelector("#add-best-targets")?.addEventListener("click", async () => {
    const computed = computeState(state);
    for (const target of computed.bestTargets.slice(0, 8)) {
      await addTargetHr(state, target.hr, false);
    }
    state.notice = "Added the top visible targets.";
    render(root, state);
  });
  root.querySelector("#optimize-target-route")?.addEventListener("click", async () => {
    const computed = computeState(state);
    state.targetList = optimizeTargetRoute(state.targetList, computed.objects);
    state.activeTargetIndex = 0;
    state.notice = "Target route sorted west-to-east across the current sky.";
    await saveTargetList(state.targetList);
    render(root, state);
  });
  root.querySelectorAll<HTMLButtonElement>("[data-focus-hr]").forEach((button) => {
    button.addEventListener("click", () => {
      focusTargetHr(root, state, Number(button.dataset.focusHr));
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-move-hr]").forEach((button) => {
    button.addEventListener("click", async () => {
      const hr = Number(button.dataset.moveHr);
      const direction = button.dataset.moveDirection === "up" ? -1 : 1;
      state.targetList = moveTargetHr(state.targetList, hr, direction);
      state.activeTargetIndex = Math.max(0, state.targetList.findIndex((item) => item.hr === hr));
      await saveTargetList(state.targetList);
      state.notice = "Target order updated.";
      render(root, state);
    });
  });
  root.querySelectorAll<HTMLButtonElement>("[data-remove-hr]").forEach((button) => {
    button.addEventListener("click", async () => {
      state.targetList = state.targetList.filter((item) => item.hr !== Number(button.dataset.removeHr));
      state.activeTargetIndex = clampIndex(state.activeTargetIndex, state.targetList.length);
      state.notice = "Target removed.";
      await saveTargetList(state.targetList);
      render(root, state);
    });
  });
  root.querySelector("#tour-first")?.addEventListener("click", () => {
    state.activeTargetIndex = 0;
    focusTargetIndex(root, state, state.activeTargetIndex);
  });
  root.querySelector("#tour-prev")?.addEventListener("click", () => {
    state.activeTargetIndex = wrapIndex(state.activeTargetIndex - 1, state.targetList.length);
    focusTargetIndex(root, state, state.activeTargetIndex);
  });
  root.querySelector("#tour-next")?.addEventListener("click", () => {
    state.activeTargetIndex = wrapIndex(state.activeTargetIndex + 1, state.targetList.length);
    focusTargetIndex(root, state, state.activeTargetIndex);
  });
  root.querySelector("#copy-handoff")?.addEventListener("click", async () => {
    await copyHandoff(state, root);
  });
  root.querySelector("#download-handoff")?.addEventListener("click", () => {
    downloadHandoff(state, root);
  });
  root.querySelector("#clear-targets")?.addEventListener("click", async () => {
    state.targetList = [];
    state.activeTargetIndex = 0;
    state.notice = "Target list cleared.";
    await saveTargetList(state.targetList);
    render(root, state);
  });
}

function refreshSearchResults(root: HTMLElement, state: AppState): void {
  const resultList = root.querySelector<HTMLElement>(".result-list");
  if (!resultList) return;
  const computed = computeState(state);
  resultList.innerHTML =
    computed.searchResults.map((result) => resultTemplate(result)).join("") ||
    `<p class="muted">Type a star, planet, HR number, or constellation.</p>`;
  bindSearchResultButtons(root, state);
}

function bindSearchResultButtons(root: HTMLElement, state: AppState): void {
  root.querySelectorAll<HTMLButtonElement>("[data-result-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const computed = computeState(state);
      const resultId = button.dataset.resultId ?? "";
      const object = computed.objects.find((candidate) => candidate.id === resultId);
      if (object) {
        selectObject(root, state, object);
        return;
      } else if (resultId.startsWith("const:")) {
        const selection = constellationSelection(state.catalog, computed.stars, resultId, state.viewport);
        if (selection) {
          selectObject(root, state, selection);
          return;
        }
      }
      render(root, state);
    });
  });
}

async function addTargetHr(state: AppState, hr: number, renderNotice = true): Promise<void> {
  if (!Number.isInteger(hr) || state.targetList.some((item) => item.hr === hr)) return;
  state.targetList = [...state.targetList, { hr, addedAt: Date.now() }];
  state.activeTargetIndex = state.targetList.length - 1;
  if (renderNotice) state.notice = `Added HR ${hr} to target list.`;
  await saveTargetList(state.targetList);
}

function moveTargetHr(items: TargetListItem[], hr: number, direction: -1 | 1): TargetListItem[] {
  const index = items.findIndex((item) => item.hr === hr);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= items.length) return items;
  const next = [...items];
  [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
  return next;
}

function focusTargetHr(root: HTMLElement, state: AppState, hr: number): void {
  const index = state.targetList.findIndex((item) => item.hr === hr);
  if (index >= 0) state.activeTargetIndex = index;
  const computed = computeState(state);
  const object = computed.objects.find((candidate) => candidate.hr === hr);
  if (!object) {
    state.notice = `HR ${hr} is not in the current catalog.`;
    render(root, state);
    return;
  }
  state.notice = `Focused ${object.label}.`;
  selectObject(root, state, object);
}

function focusTargetIndex(root: HTMLElement, state: AppState, index: number): void {
  const item = state.targetList[index];
  if (!item) {
    state.notice = "No targets to tour.";
    render(root, state);
    return;
  }
  focusTargetHr(root, state, item.hr);
}

function clampIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(length - 1, Math.max(0, index));
}

function wrapIndex(index: number, length: number): number {
  if (length <= 0) return 0;
  return ((index % length) + length) % length;
}

async function copyHandoff(state: AppState, root: HTMLElement): Promise<void> {
  const handoff = handoffForState(state);
  if (!handoff) {
    state.notice = "Add at least one target before copying.";
    render(root, state);
    return;
  }

  try {
    await navigator.clipboard.writeText(handoff);
    state.notice = "Handoff payload copied.";
  } catch {
    state.notice = "Copy blocked by the browser; use the payload text box.";
  }
  render(root, state);
}

function downloadHandoff(state: AppState, root: HTMLElement): void {
  const handoff = handoffForState(state);
  if (!handoff) {
    state.notice = "Add at least one target before downloading.";
    render(root, state);
    return;
  }

  const blob = new Blob([`${handoff}\n`], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `skyview-targets-${formatFileDate(state.viewDate)}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
  state.notice = "Handoff file downloaded.";
  render(root, state);
}

function handoffForState(state: AppState): string {
  const ids = state.targetList.map((item) => item.hr);
  if (ids.length === 0) return "";
  return encodeHandoff({
    v: 1,
    lat: state.observer.lat,
    lon: state.observer.lon,
    t: Math.floor(state.viewDate.getTime() / 1000),
    ids,
  });
}

async function renderQr(root: HTMLElement, handoff: string): Promise<void> {
  const canvas = root.querySelector<HTMLCanvasElement>("#handoff-canvas");
  if (!canvas || !handoff) {
    if (canvas) canvas.hidden = true;
    return;
  }
  canvas.hidden = false;
  await QRCode.toCanvas(canvas, handoff, {
    margin: 1,
    width: 164,
    color: {
      dark: "#07111f",
      light: "#f8fbff",
    },
  });
}

function resultTemplate(result: SearchResult): string {
  return `
    <button class="result-row" type="button" data-result-id="${result.id}">
      <strong>${escapeHtml(result.label)}</strong>
      <span>${escapeHtml(result.subtitle)}</span>
    </button>
  `;
}

function observationCardTemplate(
  object: SkyObjectPosition,
  state: AppState,
  targetIds: number[],
): string {
  const isStar = object.kind === "star" && object.hr !== undefined;
  const inTargetList = isStar && targetIds.includes(object.hr!);
  return `
    <div class="observation-card">
      <button class="observation-select" type="button" data-select-id="${object.id}">
        <strong>${escapeHtml(object.label)}</strong>
        <small>${escapeHtml(viewingAdvice(object, state.observer, state.viewDate, state.mode))}</small>
      </button>
      ${isStar ? `
        <button class="icon-button observation-add ${inTargetList ? "is-added" : ""}" type="button" data-add-hr="${object.hr}" title="${inTargetList ? "Already in target plan" : "Add to target plan"}" ${inTargetList ? "disabled" : ""}>${inTargetList ? icon("check") : icon("plus")}</button>
      ` : ""}
    </div>
  `;
}

function constellationCardTemplate(constellation: ConstellationPick): string {
  return `
    <button class="constellation-card" type="button" data-result-id="${constellation.id}">
      <strong>${escapeHtml(constellation.name)}</strong>
      <span>${constellation.visibleStars} guide stars | ${escapeHtml(plainDirectionLabel(constellation.altitudeDeg))}</span>
      <small>${escapeHtml(constellation.shapeHint)}</small>
    </button>
  `;
}

function targetPlanTemplate(state: AppState, objects: SkyObjectPosition[], handoff: string): string {
  return `
    <section class="panel target-panel">
      <div class="panel-title">
        <span>Target Plan</span>
        <span class="panel-actions">
          <button id="optimize-target-route" type="button" ${state.targetList.length < 2 ? "disabled" : ""}>Optimize route</button>
          <button id="clear-targets" type="button">Clear</button>
        </span>
      </div>
      <div class="route-summary">
        ${routeSummaryTemplate(state, objects)}
      </div>
      <div class="tour-controls">
        <span class="tour-label">Tour targets</span>
        <button class="icon-button" id="tour-first" type="button" title="First target" ${state.targetList.length === 0 ? "disabled" : ""}>${icon("skipStart")}</button>
        <button class="icon-button" id="tour-prev" type="button" title="Previous target" ${state.targetList.length === 0 ? "disabled" : ""}>${icon("prev")}</button>
        <button class="icon-button" id="tour-next" type="button" title="Next target" ${state.targetList.length === 0 ? "disabled" : ""}>${icon("next")}</button>
      </div>
      <div class="target-list">
        ${targetListTemplate(state, objects)}
      </div>
      <div class="handoff-box">
        <div>
          <strong>Android handoff</strong>
          <span>Exports HR numbers only, with location and time.</span>
        </div>
        <canvas id="handoff-canvas" class="handoff-canvas" aria-label="Target handoff QR"></canvas>
        <textarea id="handoff-code" readonly placeholder="Add stars to create a handoff payload.">${handoff}</textarea>
        <div class="export-actions">
          <button id="copy-handoff" class="secondary" type="button" ${handoff ? "" : "disabled"}>Copy</button>
          <button id="download-handoff" class="primary" type="button" ${handoff ? "" : "disabled"}>Download</button>
        </div>
      </div>
    </section>
  `;
}

function routeSummaryTemplate(state: AppState, objects: SkyObjectPosition[]): string {
  if (state.targetList.length === 0) {
    return `<p class="muted">Add targets from Tonight or Object. The route will become your Android handoff plan.</p>`;
  }

  const available = state.targetList
    .map((item) => objects.find((object) => object.hr === item.hr))
    .filter((object): object is SkyObjectPosition => object !== undefined);
  const visibleCount = available.filter((object) => object.altitudeDeg >= 5).length;
  const route = available
    .slice(0, 3)
    .map((object) => cardinalLabel(object.azimuthDeg))
    .join(" -> ");

  return `
    <div class="route-stat">
      <b>${state.targetList.length}</b>
      <span>targets</span>
    </div>
    <div class="route-stat">
      <b>${visibleCount}</b>
      <span>visible now</span>
    </div>
    <div class="route-stat wide">
      <b>${escapeHtml(route || "Waiting")}</b>
      <span>first sky sweep</span>
    </div>
  `;
}

function timePresetButtons(viewDate: Date): string {
  const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), viewDate.getDate());
  const presets = [
    ["Evening", 20],
    ["Late", 22],
    ["Midnight", 24],
    ["Pre-dawn", 28],
  ] as const;

  return presets
    .map(([label, hour]) => {
      const epoch = start.getTime() + hour * 60 * 60 * 1000;
      return `<button class="preset-button" type="button" data-time-preset="${epoch}">${label}</button>`;
    })
    .join("");
}

function visibleConstellationPicks(
  catalog: SkyCatalog,
  stars: StarPosition[],
  limit: number,
): ConstellationPick[] {
  const starsByHr = new Map(stars.map((star) => [star.hr, star]));
  return catalog.constellations
    .map((constellation) => {
      const hrs = new Set(constellation.lines.flatMap(([startHr, endHr]) => [startHr, endHr]));
      const constellationStars = [...hrs]
        .map((hr) => starsByHr.get(hr))
        .filter((star): star is StarPosition => star !== undefined);
      const visibleStars = constellationStars.filter((star) => star.altitudeDeg >= 8).length;
      const altitudeDeg =
        constellationStars.length > 0
          ? constellationStars.reduce((sum, star) => sum + star.altitudeDeg, 0) / constellationStars.length
          : 0;
      const brightScore = constellationStars.reduce((sum, star) => sum + Math.max(0, 5 - star.magnitude), 0);
      return {
        id: `const:${constellation.iau}`,
        name: constellation.name,
        shapeHint: constellation.shapeHint,
        altitudeDeg,
        visibleStars,
        score: visibleStars * 8 + Math.max(0, altitudeDeg) * 0.3 + brightScore,
      };
    })
    .filter((pick) => pick.visibleStars >= 2)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((pick) => ({
      id: pick.id,
      name: pick.name,
      shapeHint: pick.shapeHint,
      altitudeDeg: pick.altitudeDeg,
      visibleStars: pick.visibleStars,
    }));
}

function viewingAdvice(
  object: SkyObjectPosition,
  observer: ObserverLocation,
  viewDate: Date,
  mode: ExperienceMode,
): string {
  if (mode === "advanced") {
    return `Alt ${formatDegrees(object.altitudeDeg)} | Az ${formatDegrees(object.azimuthDeg)} | mag ${formatMagnitudeLabel(object.magnitude)}`;
  }

  const where = `${plainCardinalLabel(object.azimuthDeg)}, ${plainDirectionLabel(object.altitudeDeg)}`;
  if (object.kind === "star") {
    return `${where}. ${bestTimeAdviceForStar(object as StarPosition, observer, viewDate)}`;
  }
  return `${where}. ${object.altitudeDeg >= 5 ? "Good to identify now." : "Wait until it clears the horizon."}`;
}

function bestTimeAdviceForStar(star: StarPosition, observer: ObserverLocation, viewDate: Date): string {
  const samples = [0, 1, 2, 3, 4, 5, 6].map((offsetHours) => {
    const date = new Date(viewDate.getTime() + offsetHours * 60 * 60 * 1000);
    const horizontal = starHorizontal(star.star, observer, date);
    return { date, altitudeDeg: horizontal.altitudeDeg };
  });
  const best = samples.reduce((winner, sample) => (sample.altitudeDeg > winner.altitudeDeg ? sample : winner));
  if (best.altitudeDeg < 5) return "Not a good target in the next few hours.";
  if (Math.abs(best.date.getTime() - viewDate.getTime()) < 20 * 60 * 1000) return "Best right now.";
  return `Best around ${best.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.`;
}

function plainCardinalLabel(azimuthDeg: number): string {
  const label = cardinalLabel(azimuthDeg);
  switch (label) {
    case "N":
      return "look north";
    case "NE":
      return "look northeast";
    case "E":
      return "look east";
    case "SE":
      return "look southeast";
    case "S":
      return "look south";
    case "SW":
      return "look southwest";
    case "W":
      return "look west";
    default:
      return "look northwest";
  }
}

function plainDirectionLabel(altitudeDeg: number): string {
  if (altitudeDeg >= 65) return "very high";
  if (altitudeDeg >= 35) return "high in the sky";
  if (altitudeDeg >= 12) return "above the horizon";
  if (altitudeDeg >= 0) return "low near the horizon";
  return "below the horizon";
}

function cardinalLabel(azimuthDeg: number): string {
  const labels = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return labels[Math.floor((normalizeDegrees(azimuthDeg) + 22.5) / 45) % labels.length] ?? "N";
}

function formatMagnitudeLabel(value: number): string {
  return Number.isFinite(value) ? value.toFixed(1) : "N/A";
}

function optimizeTargetRoute(items: TargetListItem[], objects: SkyObjectPosition[]): TargetListItem[] {
  const objectsByHr = new Map(objects.filter((object) => object.hr !== undefined).map((object) => [object.hr!, object]));
  return [...items].sort((a, b) => {
    const objectA = objectsByHr.get(a.hr);
    const objectB = objectsByHr.get(b.hr);
    if (!objectA || !objectB) return a.hr - b.hr;
    return objectA.azimuthDeg - objectB.azimuthDeg || objectB.altitudeDeg - objectA.altitudeDeg;
  });
}

function selectedTemplate(
  selected: SkyObjectPosition,
  catalog: SkyCatalog,
  riseSet?: { rise?: Date; set?: Date; transit?: Date },
  observer?: ObserverLocation,
  viewDate?: Date,
  mode: ExperienceMode = "beginner",
): string {
  const concepts = selected.conceptIds
    .map((id) => catalog.concepts.find((concept) => concept.id === id))
    .filter((concept) => concept !== undefined);
  return `
    <div class="panel-title">
      <span>${escapeHtml(selected.label)}</span>
      <span class="panel-actions">
        <button id="focus-selected" class="icon-button" type="button" title="Focus">${icon("focus")}</button>
        ${selected.kind === "star" ? `<button id="add-target" class="primary" type="button">Add target</button>` : ""}
      </span>
    </div>
    <div class="object-guidance">
      <strong>${mode === "advanced" ? "Position" : "How to find it"}</strong>
      <span>${observer && viewDate ? escapeHtml(viewingAdvice(selected, observer, viewDate, mode)) : escapeHtml(selected.subtitle)}</span>
    </div>
    ${mode === "advanced" ? objectDataTemplate(selected) : beginnerObjectSummaryTemplate(selected)}
    <p>${escapeHtml(selected.fact ?? "Select an object to see contextual sky notes.")}</p>
    ${riseSet ? `
      <div class="stats-grid">
        <span><b>Rise</b>${formatOptionalTime(riseSet.rise)}</span>
        <span><b>Transit</b>${formatOptionalTime(riseSet.transit)}</span>
        <span><b>Set</b>${formatOptionalTime(riseSet.set)}</span>
      </div>
    ` : ""}
    ${selected.kind === "star" && observer && viewDate ? selectedTimelineTemplate(selected as StarPosition, observer, viewDate) : ""}
    <div class="concept-list">
      ${concepts.map((concept) => `
        <details>
          <summary>${escapeHtml(concept.title)}</summary>
          <p>${escapeHtml(concept.body)}</p>
        </details>
      `).join("")}
    </div>
  `;
}

function beginnerObjectSummaryTemplate(selected: SkyObjectPosition): string {
  return `
    <div class="object-data-grid compact">
      <span><b>Visibility</b>${escapeHtml(visibilityPhrase(selected.altitudeDeg))}</span>
      <span><b>Direction</b>${escapeHtml(plainCardinalLabel(selected.azimuthDeg).replace("look ", ""))}</span>
      <span><b>Height</b>${escapeHtml(plainDirectionLabel(selected.altitudeDeg))}</span>
      <span><b>Brightness</b>${escapeHtml(formatMagnitudeLabel(selected.magnitude))}</span>
    </div>
  `;
}

function selectedTimelineTemplate(star: StarPosition, observer: ObserverLocation, viewDate: Date): string {
  const offsets = [0, 2, 4, 6];
  return `
    <div class="mini-timeline">
      ${offsets.map((offset) => {
        const date = new Date(viewDate.getTime() + offset * 60 * 60 * 1000);
        const horizontal = starHorizontal(star.star, observer, date);
        return `
          <span>
            <b>${offset === 0 ? "Now" : `+${offset}h`}</b>
            ${visibilityPhrase(horizontal.altitudeDeg)} | ${directionLabel(horizontal)}
          </span>
        `;
      }).join("")}
    </div>
  `;
}

function objectDataTemplate(selected: SkyObjectPosition): string {
  const rows: Array<[string, string]> = [
    ["Altitude", formatDegrees(selected.altitudeDeg)],
    ["Azimuth", formatDegrees(selected.azimuthDeg)],
    ["Magnitude", Number.isFinite(selected.magnitude) ? selected.magnitude.toFixed(2) : "N/A"],
    ["Kind", selected.kind === "body" ? "Solar system" : selected.kind],
  ];

  if (selected.kind === "star") {
    const star = selected as StarPosition;
    rows.splice(
      0,
      0,
      ["HR", String(star.hr)],
      ["Designation", star.star.designation || "N/A"],
    );
    rows.push(["Spectral", star.star.spectralType || "Unknown"]);
    if (star.star.hd !== null) {
      rows.push(["HD", String(star.star.hd)]);
    }
  }

  return `
    <div class="object-data-grid">
      ${rows.map(([label, value]) => `
        <span>
          <b>${escapeHtml(label)}</b>
          ${escapeHtml(value)}
        </span>
      `).join("")}
    </div>
  `;
}

function emptySelectionTemplate(): string {
  return `
    <div class="panel-title"><span>Object Lab</span></div>
    <p class="object-subtitle">Click the sky or search for an object.</p>
    <p>Selection opens catalog facts, current altitude and direction, and learning chips tied to the object.</p>
  `;
}

function targetListTemplate(state: AppState, objects: SkyObjectPosition[]): string {
  if (state.targetList.length === 0) {
    return `<p class="muted">Add stars from the Object Lab. Handoff exports HR numbers only.</p>`;
  }
  return state.targetList.map((item, index) => {
    const object = objects.find((candidate) => candidate.hr === item.hr);
    const active = index === state.activeTargetIndex;
    const detail = object
      ? viewingAdvice(object, state.observer, state.viewDate, state.mode)
      : `HR ${item.hr} is not visible in the current catalog view.`;
    return `
      <div class="target-row ${active ? "active" : ""}">
        <div class="target-main">
          <span>${index + 1}. ${escapeHtml(object?.label ?? `HR ${item.hr}`)}</span>
          <small>${escapeHtml(detail)}</small>
        </div>
        <div class="target-actions">
          <button class="icon-button" type="button" data-focus-hr="${item.hr}" title="Focus">${icon("focus")}</button>
          <button class="icon-button" type="button" data-move-hr="${item.hr}" data-move-direction="up" title="Move up" ${index === 0 ? "disabled" : ""}>${icon("up")}</button>
          <button class="icon-button" type="button" data-move-hr="${item.hr}" data-move-direction="down" title="Move down" ${index === state.targetList.length - 1 ? "disabled" : ""}>${icon("down")}</button>
          <button class="icon-button is-danger" type="button" data-remove-hr="${item.hr}" title="Remove">${icon("remove")}</button>
        </div>
      </div>
    `;
  }).join("");
}

function bindCanvasInteractions(
  root: HTMLElement,
  canvas: HTMLCanvasElement,
  state: AppState,
  initialHits: CanvasHit[],
): void {
  let hits = initialHits;
  let drag:
    | {
        pointerId: number;
        startX: number;
        startY: number;
        lastX: number;
        lastY: number;
        moved: boolean;
      }
    | undefined;

  canvas.addEventListener("pointerdown", (event) => {
    cancelFocusAnimation();
    canvas.setPointerCapture(event.pointerId);
    canvas.classList.add("is-dragging");
    drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      moved: false,
    };
  });

  canvas.addEventListener("pointermove", (event) => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();

    const deltaX = event.clientX - drag.lastX;
    const deltaY = event.clientY - drag.lastY;
    const totalMove = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    drag = {
      ...drag,
      lastX: event.clientX,
      lastY: event.clientY,
      moved: drag.moved || totalMove > 5,
    };

    const rect = canvas.getBoundingClientRect();
    state.viewport = panViewport(state.viewport, deltaX, deltaY, rect.width, rect.height);
    hits = redrawSkyOnly(root, state);
  });

  canvas.addEventListener("pointerup", (event) => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    canvas.releasePointerCapture(event.pointerId);
    canvas.classList.remove("is-dragging");

    if (!drag.moved) {
      const rect = canvas.getBoundingClientRect();
      const hit = nearestCanvasObject(hits, event.clientX - rect.left, event.clientY - rect.top);
      if (hit) {
        selectObject(root, state, hit.object);
      }
    }
    drag = undefined;
  });

  canvas.addEventListener("pointercancel", (event) => {
    if (!drag || drag.pointerId !== event.pointerId) return;
    canvas.classList.remove("is-dragging");
    drag = undefined;
  });

  canvas.addEventListener(
    "wheel",
    (event) => {
      event.preventDefault();
      cancelFocusAnimation();
      state.viewport = zoomViewport(state.viewport, event.deltaY < 0);
      hits = redrawSkyOnly(root, state);
    },
    { passive: false },
  );
}

function redrawSkyOnly(root: HTMLElement, state: AppState): CanvasHit[] {
  const canvas = root.querySelector<HTMLCanvasElement>("#sky-canvas");
  if (!canvas) return [];

  const computed = computeSkyState(state);
  state.selected = resolveSelected(state.selected, computed.objects, state.catalog, state.viewport);

  return drawSky({
    canvas,
    catalog: state.catalog,
    stars: computed.stars,
    bodies: computed.bodies,
    selected: state.selected,
    targetHrs: state.targetList.map((item) => item.hr),
    viewport: state.viewport,
    showGuideLines: state.showGuideLines,
    showBelowHorizon: state.showBelowHorizon,
  });
}

function selectObject(root: HTMLElement, state: AppState, object: SkyObjectPosition): void {
  state.selected = object;
  state.activeTab = "object";
  render(root, state);
  animateViewportTo(root, state, object);
}

function animateViewportTo(root: HTMLElement, state: AppState, object: SkyObjectPosition): void {
  cancelFocusAnimation();
  const from = state.viewport;
  const to = targetViewportForObject(state.viewport, object);
  const startedAt = performance.now();
  const durationMs = 620;

  const animate = (now: number) => {
    const progress = Math.min(1, (now - startedAt) / durationMs);
    state.viewport = interpolateViewport(from, to, easeOutCubic(progress));
    redrawSkyOnly(root, state);

    if (progress < 1) {
      focusAnimationFrame = window.requestAnimationFrame(animate);
    } else {
      focusAnimationFrame = undefined;
    }
  };

  focusAnimationFrame = window.requestAnimationFrame(animate);
}

function cancelFocusAnimation(): void {
  if (focusAnimationFrame !== undefined) {
    window.cancelAnimationFrame(focusAnimationFrame);
    focusAnimationFrame = undefined;
  }
}

function setPlayback(root: HTMLElement, state: AppState, playing: boolean): void {
  if (!playing) {
    stopPlayback(state);
    render(root, state);
    return;
  }

  stopPlayback();
  state.isPlaying = true;
  playbackTimer = window.setInterval(() => {
    state.viewDate = new Date(state.viewDate.getTime() + PlaybackStepMillis);
    render(root, state);
  }, PlaybackIntervalMillis);
  render(root, state);
}

function stopPlayback(state?: AppState): void {
  if (playbackTimer !== undefined) {
    window.clearInterval(playbackTimer);
    playbackTimer = undefined;
  }
  if (state) state.isPlaying = false;
}

function stepTime(root: HTMLElement, state: AppState, minutes: number): void {
  stopPlayback(state);
  state.viewDate = new Date(state.viewDate.getTime() + minutes * 60 * 1000);
  render(root, state);
}

function resolveSelected(
  selected: SkyObjectPosition | undefined,
  objects: SkyObjectPosition[],
  catalog: SkyCatalog,
  viewport: SkyViewportState,
): SkyObjectPosition | undefined {
  if (!selected) return undefined;
  const object = objects.find((candidate) => candidate.id === selected.id);
  if (object) return object;
  if (selected.id.startsWith("const:")) {
    return constellationSelection(catalog, objects.filter(isStarPosition), selected.id, viewport);
  }
  return selected;
}

function constellationSelection(
  catalog: SkyCatalog,
  stars: StarPosition[],
  id: string,
  viewport: SkyViewportState,
): SkyObjectPosition | undefined {
  const constellation = catalog.constellations.find((item) => `const:${item.iau}` === id);
  if (!constellation) return undefined;

  const hrSet = new Set(constellation.lines.flatMap(([startHr, endHr]) => [startHr, endHr]));
  const constellationStars = stars.filter((star) => hrSet.has(star.hr));
  const sinSum = constellationStars.reduce((sum, star) => sum + Math.sin((star.azimuthDeg * Math.PI) / 180), 0);
  const cosSum = constellationStars.reduce((sum, star) => sum + Math.cos((star.azimuthDeg * Math.PI) / 180), 0);
  const altitudeSum = constellationStars.reduce((sum, star) => sum + star.altitudeDeg, 0);

  return {
    id,
    label: constellation.name,
    kind: "constellation",
    magnitude: Number.POSITIVE_INFINITY,
    altitudeDeg: constellationStars.length > 0 ? altitudeSum / constellationStars.length : viewport.centerAltitudeDeg,
    azimuthDeg:
      constellationStars.length > 0 ? normalizeDegrees((Math.atan2(sinSum, cosSum) * 180) / Math.PI) : viewport.centerAzimuthDeg,
    subtitle: `Constellation | ${constellation.shapeHint}`,
    fact: constellation.shapeHint,
    conceptIds: ["altaz"],
  };
}

function isStarPosition(object: SkyObjectPosition): object is StarPosition {
  return object.kind === "star";
}

function easeOutCubic(value: number): number {
  return 1 - Math.pow(1 - value, 3);
}

function relativeHoursFromNow(date: Date): number {
  const hours = (date.getTime() - Date.now()) / (60 * 60 * 1000);
  return Math.min(12, Math.max(-12, hours));
}

function toDateTimeLocalValue(date: Date): string {
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function formatSkyDateTime(date: Date): string {
  return date.toLocaleString([], {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });
}

function formatDegrees(value: number): string {
  return `${value.toFixed(1)} deg`;
}

function formatFileDate(date: Date): string {
  return date.toISOString().slice(0, 16).replace(/[-:T]/g, "");
}

function formatOptionalTime(date?: Date): string {
  return date ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "N/A";
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (char) => {
    switch (char) {
      case "&":
        return "&amp;";
      case "<":
        return "&lt;";
      case ">":
        return "&gt;";
      case "\"":
        return "&quot;";
      default:
        return "&#39;";
    }
  });
}
